import { h, Component, ComponentChild } from 'preact';
import Video from '..';
import { isFeatureEnable } from '../../../../common/js/features-enabling';
import { canInstallShortcut } from '../../../../common/js/shortcut-utils';
import VASTClient from '../../lib/vast/vast-client';
import translations from '../../lib/translations';
import { sendOmidTrackingEvents } from '../../../../common/js/TrackAnalyticsHelper';
import {
  initOmidAdEvents,
  initOmidMediaEvents,
  setOmidImpressionDelay
} from '../../../../common/js/omid/omid';
import { isMediumRectangleAdUnit } from '../../../../common/js/adUnit';

import { RootStore } from '../../../../common/js/stores/root.store';
import {
  TRACKING_FRAMEWORK_OMID,
  TrackingFramework
} from '../../../../common/js/tracking-helper';
import { isWeb } from '../../../../common/js/environment-utils';
import { VastIconType } from '../../lib/vast/vast-icon';
import TranslatorProvider from '../../../../common/components/translator-provider';
import { createUnmountOnCall } from '../../../../common/js/dom-utils';
import {
  EVENT_OMID_VAST_TRACKING,
  EVENT_OMID_VIDEO_TRACKING,
  getOmidTrackingEvents,
  setOmidTrackingEvent
} from '../../../../common/js/omid/omid-tracking';
import { parseOmidVideoTrackers } from '../../../../common/js/omid/omid-config-parser';
import { Actions } from '../../lib/actions';

interface Options {
  context: AdContext;
  container: HTMLElement;
  callbacks?: {
    onClick?: () => void;
  };
}

export interface VideoContainerProps {
  config: FormatConfig;
  actions: Actions;
  options: Options;
  containerId: string;
  trackingFramework: TrackingFramework;
}

interface State {}

class VideoContainer extends Component<VideoContainerProps, State> {
  wrappedActions: Actions;
  onVideoPlayerReady: (videoPlayer: HTMLVideoElement) => void;
  vast: VASTClient;

  constructor(props: VideoContainerProps) {
    super(props);

    this.vast = RootStore.getInstance().videoStore.vast!;

    const { config, actions, options } = props;
    this.vast.onEvent(actions.onVideoEvent);

    const unmountOnCall = createUnmountOnCall(options.container);

    const onClickMethod = this.shouldUseUnmountMethods(config, options)
      ? unmountOnCall.bind(null, actions.onClick)
      : actions.onClick;

    const onOptoutMethod = this.shouldUseUnmountMethods(config, options)
      ? unmountOnCall.bind(null, actions.onOptout)
      : actions.onOptout;

    const wrappedActions: Actions = {
      ...actions,
      handleOgyOnCloseSystem: unmountOnCall.bind(
        null,
        actions.handleOgyOnCloseSystem
      ),
      onClick: onClickMethod,
      onError: unmountOnCall.bind(null, actions.onError),
      onClose: unmountOnCall.bind(null, actions.onClose),
      onOptout: onOptoutMethod
    };

    if (isFeatureEnable('rageQuitEvent')) {
      const adContainerStore = RootStore.getInstance().adContainerStore;

      adContainerStore.adContainerPort.addEventListener(
        'ogyOnCloseSystem',
        function onCloseSystemListener() {
          adContainerStore.adContainerPort.removeEventListener(
            'ogyOnCloseSystem',
            onCloseSystemListener
          );
          if (
            adContainerStore.state.value !== 'loading' &&
            options.context &&
            (options.context.adUnitType === 'interstitial' ||
              options.context.adUnitType === 'optin_video')
          ) {
            wrappedActions.handleOgyOnCloseSystem();
          }
        }
      );
    }

    this.wrappedActions = wrappedActions;

    this.onVideoPlayerReady = this.handleVideoPlayerReady.bind(this);
  }

  shouldUseUnmountMethods(config: FormatConfig, options: Options): boolean {
    if (isWeb()) {
      return !!config.shouldCallMraidCloseOnClick;
    }

    return (
      !isMediumRectangleAdUnit(options.context.adUnitType || '') &&
      !RootStore.getInstance().adContainerStore.canOpenStoreKit(
        options.context as any
      ) &&
      !(options.context as any).isUserAcquisition
    );
  }

  handleVideoPlayerReady(videoPlayer: HTMLVideoElement): void {
    const { config, options, trackingFramework } = this.props;
    const omidVideoTrackers = parseOmidVideoTrackers(
      config.omidVideoTrackers as any,
      options.context.tcfv2
    );

    const jsVerificationResources = [
      ...this.vast.getJSVerificationResources(),
      ...omidVideoTrackers
    ];

    // Set tracking event to be sent to ms-ads-monitoring-events
    // in order to know the OMID configuration at runtime
    setOmidTrackingEvent(
      EVENT_OMID_VAST_TRACKING,
      this.vast.getJSVerificationResources().length > 0
    );
    setOmidTrackingEvent(
      EVENT_OMID_VIDEO_TRACKING,
      omidVideoTrackers.length > 0
    );

    if (trackingFramework === TRACKING_FRAMEWORK_OMID) {
      // OMID init
      initOmidAdEvents(
        options.container,
        config.omidTrackers as any,
        jsVerificationResources,
        true,
        true,
        videoPlayer,
        options.context && options.context.enableOmidViewabilityTracks === true
      );
      setOmidImpressionDelay(config.omidImpressionDelay);

      initOmidMediaEvents();
    }

    sendOmidTrackingEvents(getOmidTrackingEvents());
  }

  render(): ComponentChild {
    const { config, options, containerId } = this.props;

    return (
      <TranslatorProvider
        locale={config.locale as string}
        translations={translations}
        customTranslations={config.customTranslations}
      >
        <div data-zone="video">
          <Video
            containerId={containerId}
            config={config}
            context={options.context as any}
            actions={this.wrappedActions}
            vastIcon={this.vast.getVastIcon() as VastIconType}
            canInstallIcon={canInstallShortcut(config as any)}
            onVideoPlayerReady={this.onVideoPlayerReady}
            {...(this.wrappedActions as any)}
          />
        </div>
      </TranslatorProvider>
    );
  }
}

export default VideoContainer;
